import {
  AhaEditorUtils,
  type AhaEditorV2Controller,
  type EmbeddedExtensionPanelPayload,
  type EmbeddedReportPayload,
  slate,
  Tool,
  type ToolArgsWithController,
  isEmbeddedReportPlaceholderElement,
} from '@aha-app/aha-editor-v2';
import {
  chooseReport,
  type NewEmbeddedReportPayload,
} from '../components/AddReportModal';
import { randomId } from 'javascripts/utils';
import { showAlertMessage } from 'javascripts/messages';
import { bookmarkURL } from 'components/Dashboards/helpers';
import { Panel } from 'components/Dashboards/store/records';
import { jsonFetch } from 'javascripts/fetch';

function replacePlaceholder(
  editor: slate.Editor,
  placeholder_id: string,
  payload: EmbeddedReportPayload | null
): void {
  if (payload) {
    slate.Transforms.setNodes(
      editor,
      {
        type: 'embedded-report',
        children: [{ text: '' }],
        bookmark_id: payload.bookmarkId,
        bookmark_path: bookmarkURL(new Panel(payload)),
        payload,
        resizer_width: 900,
        resizer_height: 500,
        resizer_originalWidth: 2048,
        resizer_originalHeight: 2048 / (16 / 9),
      },
      {
        at: [],
        match: n =>
          isEmbeddedReportPlaceholderElement(n) &&
          n.placeholder_id === placeholder_id,
      }
    );
  } else {
    slate.Transforms.removeNodes(editor, {
      at: [],
      match: n =>
        isEmbeddedReportPlaceholderElement(n) &&
        n.placeholder_id === placeholder_id,
    });
  }
}

function loadReport({
  editor,
  controller,
  placeholder_id,
  payload,
}: {
  editor: slate.Editor;
  controller: AhaEditorV2Controller;
  placeholder_id: string;
  payload: NewEmbeddedReportPayload;
}): void {
  if (!controller.objectId) {
    setImmediate(() => {
      replacePlaceholder(editor, placeholder_id, null);
    });
  } else if (payload.type === 'ExtensionPanel') {
    setImmediate(() => {
      replacePlaceholder(
        editor,
        placeholder_id,
        payload as EmbeddedExtensionPanelPayload
      );
    });
  } else {
    const body = { report_props: payload };
    const url = `/notes/${controller.objectId}/build_shareable_bookmark`;
    jsonFetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then(response => response.json() as Promise<EmbeddedReportPayload>)
      .then(payload => {
        replacePlaceholder(editor, placeholder_id, payload);
      })
      .catch(err => {
        showAlertMessage('There was an error loading the view.');
        console.error(err);
        replacePlaceholder(editor, placeholder_id, null);
      });
  }
}

class ToolInsertReport extends Tool {
  static toolName = 'Aha! view';
  static iconClass = 'fa-regular aha-icon aha-icon-views';

  static available({ controller }: ToolArgsWithController) {
    if (!controller.objectId) return false;
    if (window.currentAccount?.onlyDiscoveryFlavor) return false;

    return controller.ahaEditor.hasPlugin('EmbeddedReportComponentPlugin');
  }

  static insertReport({
    editor,
    controller,
    payload,
  }: {
    editor: slate.Editor;
    controller: AhaEditorV2Controller;
    payload: NewEmbeddedReportPayload;
  }): void {
    const id = randomId();

    slate.Transforms.insertNodes(editor, {
      type: 'embedded-report-placeholder',
      children: [{ text: '' }],
      placeholder_id: id,
    });

    loadReport({ editor, controller, placeholder_id: id, payload });
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    // When we show a modal, it will capture the last activeElement
    // and restore it when the modal closes. This is a problem because
    // it causes Slate's focus state to get out of sync with the
    // browser's. Calling .focus() afterward will not fix this,
    // because it is a no-op if it detects that the browser focus is
    // already the editor element. Manually blurring before we show
    // the modal will avoid this.
    AhaEditorUtils.Editor.blur(editor);
    chooseReport()
      .then(payload => {
        if (payload) {
          this.insertReport({ editor, controller, payload });
          AhaEditorUtils.Editor.focus(editor);
        }
      })
      .catch(err => {
        console.error(err);
      });
  }
}

export default ToolInsertReport;
